import type { FC } from 'react'
import React, { useEffect, useMemo, useState } from 'react'
import { Skeleton } from '@frontend/pole-ui/lib/components/Skeleton'
import { GetClientPublicationsQueryParamsType, useGetClientPublications } from '@/api/kubik/infoportal'
import { JournalSearchInput, RubricsMenu } from '@/components/composed'
import { MetaInfo, PaginationMoreButton } from '@/components/ui'
import JournalTagItem from '@/pages/Journal/components/JournalByTags/components/JournalTagItem/JournalTagItem'
import { sortArraysByDate } from '@/pages/Journal/components/JournalByTags/helpers'
import { formatLocalizedString } from '@/utils/formatUtils'
import { useResponsiveValue } from '@/utils/hooks/responsive'
import { useJournalTags } from '@/utils/infoportal/hooks/useJournalTags'
import { FilteredAllArticles } from './components/'
import { JOURNAL_TAG_ITEM_SKELETON, RESPONSIVE_TAG_CARDS_PER_PAGE } from './constants'
import { messages } from './messages'
import type { IJournalByTagsProps } from './types'
import './styles.scss'

const JournalByTags: FC<IJournalByTagsProps> = props => {
  const { tag } = props

  const [currentArticlesPage, setCurrentArticlesPage] = useState(1)

  const totalItemsPerPage = useResponsiveValue(RESPONSIVE_TAG_CARDS_PER_PAGE)
  const itemPerPage = (totalItemsPerPage / 2) * currentArticlesPage

  const { currentTag, isTagsLoaded } = useJournalTags(tag)

  const shouldShowNoPublicationsError = !currentTag && isTagsLoaded

  const {
    data: articlesByTagData,
    isFetching: isArticlesByTagLoading,
    isInitialLoading: isArticlesInitialLoading,
  } = useGetClientPublications(
    { tagId: tag, type: GetClientPublicationsQueryParamsType.ARTICLE, limit: itemPerPage },
    { query: { staleTime: Infinity, keepPreviousData: true } },
  )

  const {
    data: newsByTagData,
    isFetching: isNewsByTagLoading,
    isInitialLoading: isNewsInitialLoading,
  } = useGetClientPublications(
    { tagId: tag, type: GetClientPublicationsQueryParamsType.NEWS, limit: itemPerPage },
    { query: { staleTime: Infinity, keepPreviousData: true } },
  )

  const sortedTaggedItems = useMemo(
    () => sortArraysByDate([...(articlesByTagData?.data?.publications || []), ...(newsByTagData?.data?.publications || [])]),
    [articlesByTagData, newsByTagData],
  )

  const articlesByTagPagination = articlesByTagData?.data?.pagination
  const newsByTagPagination = newsByTagData?.data?.pagination

  const isArticleLastPage = articlesByTagPagination ? articlesByTagPagination.limit >= articlesByTagPagination.total : true
  const isNewsLastPage = newsByTagPagination ? newsByTagPagination.limit >= newsByTagPagination.total : true

  const hasNextPage = !(isArticleLastPage && isNewsLastPage)

  const shouldShowArticles =
    !isNewsInitialLoading && !isArticlesInitialLoading && sortedTaggedItems && sortedTaggedItems.length < 6 && !hasNextPage

  useEffect(() => {
    if (!articlesByTagData?.data?.publications || !newsByTagData?.data?.publications || isNewsByTagLoading || isArticlesByTagLoading) return

    if (articlesByTagData.data.publications.length + newsByTagData.data?.publications.length < totalItemsPerPage && hasNextPage) {
      setCurrentArticlesPage(p => p + 1)
    }
  }, [articlesByTagData, hasNextPage, isArticlesByTagLoading, isNewsByTagLoading, newsByTagData, totalItemsPerPage])

  return (
    <>
      {currentTag && (
        <MetaInfo
          metaInfo={{
            title: formatLocalizedString(messages.reactHelmetTitle, { tagName: currentTag.name }),
          }}
        />
      )}

      <RubricsMenu />
      <JournalSearchInput />

      <div className="page-wrapper">
        {shouldShowNoPublicationsError ? (
          <h4>{messages.tagNotFound}</h4>
        ) : (
          <>
            <h2 className="journalByTags__header">{currentTag?.name}</h2>
            <div>
              <div className="journalByTags__tagCards">
                <Skeleton
                  isLoading={(isArticlesInitialLoading || isNewsInitialLoading) && currentArticlesPage === 1}
                  skeletons={JOURNAL_TAG_ITEM_SKELETON}
                >
                  {sortedTaggedItems.map((combinedItem, index) => (
                    <JournalTagItem newsItem={combinedItem} isFirst={index === 0} key={combinedItem.id} />
                  ))}
                </Skeleton>
              </div>

              <PaginationMoreButton
                className="journalByTags__paginationMore"
                isLoading={isArticlesByTagLoading || isNewsByTagLoading}
                onClick={() => setCurrentArticlesPage(p => p + 1)}
                hasNextPage={hasNextPage}
              />
            </div>
            {shouldShowArticles && (
              <>
                <h2 className="journalByTags__articlesHeader">{messages.articlesHeader}</h2>

                <FilteredAllArticles key={tag} className="journalByTags__articles" entriesByTag={sortedTaggedItems} />
              </>
            )}
          </>
        )}
      </div>
    </>
  )
}

export default JournalByTags
